import { useCallback, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useNavigate } from "react-router-dom";
import Logo from "../../icons/Aconto_logo.png";
import "./Login.css";
import { ISignup, signupInit } from "../../models/signup";
import { submitSignupRequest } from "../../api/signup";
import { useDispatch } from "react-redux";
import { showToastMessage } from "../../actions/toastMessageActions";
import { useIntl } from "react-intl";
import Messages from "../../localization/Messages";
import { classNames } from "primereact/utils";

export const Signup = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [signupForm, setSignupForm] = useState<ISignup>(signupInit);
    const intl = useIntl();
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({
        firstName: "",
        lastName: "",
        email: "",
        companyName: "",
    });

    const validateField = useCallback(
        (fieldName: string, value: string) => {
            switch (fieldName) {
                case "firstName":
                    if (!value) {
                        return intl.formatMessage(Messages.validateFirstName);
                    }
                    break;
                case "lastName":
                    if (!value) {
                        return intl.formatMessage(Messages.validateLastName);
                    }
                    break;
                case "email":
                    if (!value) {
                        return intl.formatMessage(Messages.validateUsername);
                    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
                        return intl.formatMessage(Messages.emailValidation);
                    }
                    break;
                case "companyName":
                    if (!value) {
                        return intl.formatMessage(Messages.companyNameValidate);
                    }
                    break;
                default:
                    return "";
            }
            return "";
        },
        [intl]
    );

    const disabled = () => {
        return (
            signupForm.firstName.length === 0 ||
            signupForm.lastName.length === 0 ||
            signupForm.email.length === 0 ||
            !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(signupForm.email) ||
            signupForm.companyName.length === 0
        );
    };

    const onSignUpCLick = async () => {
        try {
            await submitSignupRequest(signupForm);
            dispatch(showToastMessage("Success! Check your inbox!", "success"));
        } catch (error) {
            dispatch(showToastMessage("Error while submitting your request.", "error"));
        } finally {
            navigate("/login");
        }
    };

    const onChange = (e: any) => {
        const fieldName = e.target.id;
        const value = e.target.value;
        setSignupForm({ ...signupForm, [fieldName]: value });

        const errorMessage = validateField(fieldName, value);
        setFieldErrors(prevErrors => ({ ...prevErrors, [fieldName]: errorMessage }));
    };

    return (
        <div className="login-container">
            <div className="center-login-container">
                <section className="login-form-container">
                    <div id="login-logo-container">
                        <img src={Logo} id="login-logo" alt="company-logo" />
                        <h2>Business administration made easy</h2>
                    </div>
                    <div className="p-inputgroup">
                        <span className="p-inputgroup-addon">
                            <i className="pi pi-user"></i>
                        </span>
                        <InputText
                            className={classNames({
                                "p-inputtext-sm": true,
                                "p-invalid": fieldErrors.firstName,
                            })}
                            id="firstName"
                            placeholder="First name"
                            value={signupForm.firstName}
                            onChange={onChange}
                        />
                    </div>
                    {fieldErrors.firstName && (
                        <small className="p-error block">{fieldErrors.firstName}</small>
                    )}

                    <div className="p-inputgroup">
                        <span className="p-inputgroup-addon">
                            <i className="pi pi-user"></i>
                        </span>
                        <InputText
                            className={classNames({
                                "p-inputtext-sm": true,
                                "p-invalid": fieldErrors.lastName,
                            })}
                            id="lastName"
                            placeholder="Last name"
                            value={signupForm.lastName}
                            onChange={onChange}
                        />
                    </div>
                    {fieldErrors.lastName && (
                        <small className="p-error block">{fieldErrors.lastName}</small>
                    )}

                    <div className="p-inputgroup">
                        <span className="p-inputgroup-addon">
                            <i className="pi pi-envelope"></i>
                        </span>
                        <InputText
                            className={classNames({
                                "p-inputtext-sm": true,
                                "p-invalid": fieldErrors.email,
                            })}
                            id="email"
                            placeholder="Email"
                            value={signupForm.email}
                            onChange={onChange}
                        />
                    </div>
                    {fieldErrors.email && (
                        <small className="p-error block">{fieldErrors.email}</small>
                    )}

                    <div className="p-inputgroup">
                        <span className="p-inputgroup-addon">
                            <i className="pi pi-home"></i>
                        </span>
                        <InputText
                            className={classNames({
                                "p-inputtext-sm": true,
                                "p-invalid": fieldErrors.companyName,
                            })}
                            id="companyName"
                            placeholder="Company name"
                            value={signupForm.companyName}
                            onChange={onChange}
                        />
                    </div>
                    {fieldErrors.companyName && (
                        <small className="p-error block">{fieldErrors.companyName}</small>
                    )}

                    <hr />

                    <Button
                        className="sign-up-button"
                        disabled={disabled()}
                        onClick={onSignUpCLick}
                        label="Sign up"
                    />
                </section>
            </div>
        </div>
    );
};
